import { useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import SeeMoreText from "./clubProfile/SeeMoreText";
import { checkNullAndUndefined, formatCurrency } from "../Utilities/utils";
import { CgSpinner } from "react-icons/cg";
import { Store } from "react-notifications-component";
import { cancelTheMembership } from "../Api/api";

export const MembershipCard = ({
    membership,
    memberships,
    setMemberships,
    refresh,
    setRefresh
}: any) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const NO_BINDING_PERIOD = "NO BINDING PERIOD";
    const bindingPeriod = membership?.binding_period
        ? membership?.binding_period === NO_BINDING_PERIOD
            ? membership?.binding_period
            : `${t("bindingPeriod")}: ${membership?.binding_period} ${
                  +membership?.binding_period > 1 ? t("months") : t("month")
              }`
        : NO_BINDING_PERIOD;

    const checkIsDatePassed = (providedMilliseconds: any) => {
        const currentMilliseconds = Date.now();
        if (currentMilliseconds > providedMilliseconds) {
            return true;
        }
        return false;
    };

    function millisecondsToUTCDate(milliseconds: any) {
        // Create a new Date object with milliseconds
        const date = new Date(milliseconds);

        // Get UTC date components
        const year = date.getUTCFullYear();
        let month: any = date.getUTCMonth() + 1; // Months are zero based, so we add 1
        let day: any = date.getUTCDate();

        // Pad month and day with leading zeros if needed
        month = month < 10 ? "0" + month : month;
        day = day < 10 ? "0" + day : day;

        // Format the date as MM-DD-YYYY
        const formattedDate = year + "-" + month + "-" + day;

        return formattedDate;
    }

    const setEndDateText = (providedMilliseconds: any) => {
        const currentMilliseconds = Date.now();
        if (providedMilliseconds > currentMilliseconds) {
            return (
                t("yourSubscriptionWillEnd") +
                " " +
                millisecondsToUTCDate(providedMilliseconds)
            );
        }
        return t("yourSubscriptionEnded");
    };

    const handleCancelMembership = async (
        clubId: string,
        membership_id: string
    ) => {
        setMemberships(
            memberships?.map((item: any) =>
                item?._id === membership_id ? { ...item } : item
            )
        );
        setIsLoading(true);
        try {
            await cancelTheMembership(clubId, membership_id);
            setMemberships(
                memberships?.map((item: any) =>
                    item?._id === membership_id
                        ? { ...item, isLoading: false }
                        : item
                )
            );
            setIsLoading(false);
            Store.addNotification({
                title: "",
                message: `${t("membershipCancelledSuccessfully")}`,
                type: "success",
                container: "bottom-center",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: {
                    duration: 3000
                }
            });
            setRefresh(!refresh);
        } catch (error) {
            setMemberships(
                memberships?.map((item: any) =>
                    item?._id === membership_id
                        ? { ...item, isLoading: false }
                        : item
                )
            );
            Store.addNotification({
                title: "",
                message: `${t("somethingWrong")}`,
                type: "danger",
                container: "bottom-center",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: {
                    duration: 3000
                }
            });
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <div className="max-w-[400px] w-full p-5 text-center border border-secondary border-opacity-[0.05] rounded-[10px] shadow">
            <p className="capitalize font-bold">{membership?.club_name}</p>
            <div className="mt-2 mb-4 flex flex-wrap items-center justify-center gap-2 text-sm text-center font-semibold">
                <img src="/images/location.svg" alt="location" />
                <p className="capitalize">{membership?.club_address}</p>
            </div>
            <h3 className="text-[21px] font-bold">{membership.name}</h3>
            {membership?.type !== "Public" ? (
                <p className="mt-3 text-sm font-medium">
                    {t("period")}:{" "}
                    {moment
                        .utc(membership?.start_date)
                        .tz(membership?.timezone)
                        .format("DD MMM")}{" "}
                    -
                    {moment
                        .utc(membership?.end_date)
                        .tz(membership?.timezone)
                        .format("DD MMM YYYY")}
                </p>
            ) : null}
            {membership?.type === "Public" &&
                checkNullAndUndefined(membership?.player_end_datetime) &&
                membership?.period === "Monthly" && (
                    <p className="mt-3 text-sm font-medium">
                        {membership?.binding_period
                            ? bindingPeriod
                            : t("noBindingPeriod")}
                    </p>
                )}
            {membership?.information ? (
                <div className="mt-4">
                    <SeeMoreText
                        numberOfWords={80}
                        text={membership.information}
                    />
                </div>
            ) : null}
            <div className="max-w-fit mx-auto my-4 flex flex-col gap-1">
                {membership.usp_1 ? (
                    <p className="text-[15px] flex gap-2 capitalize">
                        <img src="/images/check-mark.svg" alt="check" />
                        <span> {membership.usp_1} </span>
                    </p>
                ) : null}
                {membership.usp_2 ? (
                    <p className="text-[15px] flex gap-2 capitalize">
                        <img src="/images/check-mark.svg" alt="check" />
                        <span> {membership.usp_2} </span>
                    </p>
                ) : null}
                {membership.usp_3 ? (
                    <p className="text-[15px] flex gap-2 capitalize">
                        <img src="/images/check-mark.svg" alt="check" />
                        <span> {membership.usp_3} </span>
                    </p>
                ) : null}
                {membership.usp_4 ? (
                    <p className="text-[15px] flex gap-2 capitalize">
                        <img src="/images/check-mark.svg" alt="check" />
                        <span> {membership.usp_4} </span>
                    </p>
                ) : null}
            </div>
            {membership?.type === "Public" ? (
                <p className="text-[17px] font-semibold mb-3">
                    {`${formatCurrency(membership?.price, membership?.currency?.toUpperCase())}/${
                        membership?.period === "Monthly"
                            ? t("month")?.toLowerCase()
                            : t("year")?.toLowerCase()
                    }`}
                </p>
            ) : null}
            {!checkNullAndUndefined(membership?.player_end_datetime) ? (
                <div>
                    {!checkIsDatePassed(membership?.player_start_datetime) ? (
                        <p className="font-bold text-sm">
                            {t("yourSubscriptionWillStart")}{" "}
                            {millisecondsToUTCDate(
                                membership?.player_start_datetime
                            )}
                        </p>
                    ) : null}
                    <div style={{ margin: 5 }}></div>
                    <p className="font-bold text-sm">
                        {setEndDateText(membership?.player_end_datetime)}
                    </p>
                </div>
            ) : (
                <button
                    className="mt-4 w-full h-[49px] flex justify-center items-center bg-transparent text-secondary text-[13px] font-bold border border-secondary rounded-full hover:bg-secondary hover:border-secondary hover:text-white"
                    disabled={isLoading}
                    onClick={() =>
                        handleCancelMembership(
                            membership?.club_id,
                            membership?._id
                        )
                    }
                >
                    {isLoading ? (
                        <CgSpinner className="animate-spin text-[24px]" />
                    ) : (
                        <p>{t("cancelMembership")}</p>
                    )}
                </button>
            )}
        </div>
    );
};
